/* ============================================================================
   CHEQUEO DE COBERTURA fuente → data.json (Mejora 3)
   ---------------------------------------------------------------------------
   Diff determinista post-generación: compara lo que el TRIAGE verificó en el
   fuente (.sst) contra lo que el Agente 3 emitió en el data.json. Atrapa el
   modo de fallo #1: generaciones incompletas que "parecen" bien.

   Devuelve diagnostics[] con el mismo contrato del validador/modal:
     { code, severity, message, detail? }

   Reglas (alineadas con la Tabla de Equivalencias v1.0):
   - COVERAGE_MISSING_FIELD    (warning): campo capturado en WINDOW sin campo en forms[].
   - COVERAGE_MISSING_FAMILY   (error):   family de ESCRITURA del fuente sin representación
                                          en onLoad/postCommands/actions/forms-remarks.
   - COVERAGE_MISSING_READ     (info):    el fuente lee (pnr.read) y no hay onLoad ni prefill.
   - COVERAGE_INVENTED_FAMILY  (error):   el data.json emite comandos de una family que el
                                          fuente NO tiene (inventado).
   - COVERAGE_AUTO_COMMIT      (warning): el fuente NO graba (sin pnr.commit) pero hay
                                          escrituras — el runtime agregará ER (WARN_NO_COMMIT).
   - COVERAGE_MISSING_ALERTS   (error):   el triage detectó alertas al usuario y el data.json
                                          no trae alerts[].
   ============================================================================ */
import { classifyFamily } from './triage'

const WRITE_FAMILIES = ['pnr.remark', 'pnr.ssr', 'pnr.contact', 'pnr.name', 'pnr.segment', 'pricing', 'ticketing', 'queue', 'pnr.received', 'pnr.dk']
const IGNORE_INVENTED = ['keys', 'unknown', 'pnr.commit', 'pnr.ignore', 'area', 'pnr.read']

function commandText(entry) {
  if (entry == null) return ''
  if (typeof entry === 'string') return entry
  if (typeof entry === 'object') {
    if (entry.deleteRemarks) return '5¤' // directiva de borrado → family pnr.remark
    return String(entry.command || entry.cmd || entry.text || '')
  }
  return String(entry)
}

function normalizeTemplate(text) {
  // {{campo}} y {campo} son placeholders del runtime: los sacamos para clasificar.
  return String(text || '').replace(/\{\{[^}]*\}\}/g, '').replace(/\{[A-Z_]+\}/g, '').trim()
}

function dataJsonCommands(dataJson) {
  const data = dataJson || {}
  const all = []
  ;(Array.isArray(data.onLoad) ? data.onLoad : []).forEach((e) => all.push(commandText(e)))
  ;(Array.isArray(data.postCommands) ? data.postCommands : []).forEach((e) => all.push(commandText(e)))
  ;(Array.isArray(data.actions) ? data.actions : []).forEach((a) => {
    ;(Array.isArray(a && a.commands) ? a.commands : []).forEach((e) => all.push(commandText(e)))
  })
  return all.filter((t) => String(t).trim() !== '')
}

function dataJsonFieldSet(dataJson) {
  const set = new Set()
  ;(Array.isArray(dataJson && dataJson.forms) ? dataJson.forms : []).forEach((form) => {
    ;(Array.isArray(form && form.remarks) ? form.remarks : []).forEach((r) => {
      const key = String((r && (r.campoForm || r.tag || r.campo)) || '').trim().toUpperCase()
      if (key) set.add(key)
    })
  })
  return set
}

function hasFormRemarks(dataJson) {
  return (Array.isArray(dataJson && dataJson.forms) ? dataJson.forms : []).some((form) =>
    (Array.isArray(form && form.remarks) ? form.remarks : []).some((r) => r && r.skip !== true))
}

export function checkCoverage(triage, dataJson) {
  const diagnostics = []
  if (!triage || !dataJson || typeof dataJson !== 'object') return diagnostics
  const push = (code, severity, message, detail) => diagnostics.push(detail !== undefined ? { code, severity, message, detail } : { code, severity, message })

  const sourceFamilies = Array.isArray(triage.families) ? triage.families : []
  const generatedFamilies = [...new Set(dataJsonCommands(dataJson).map((t) => classifyFamily(normalizeTemplate(t))))]

  // 1) Campos de ventana → forms[]
  const fieldSet = dataJsonFieldSet(dataJson)
  const missingFields = (Array.isArray(triage.camposVentana) ? triage.camposVentana : []).filter((v) => !fieldSet.has(v))
  missingFields.forEach((v) => {
    push('COVERAGE_MISSING_FIELD', 'warning', `El campo [${v}] se captura en una WINDOW del fuente pero no existe en forms[] del data.json.`, v)
  })

  // 2) Families de escritura del fuente sin representación
  const coveredForRemarks = generatedFamilies.includes('pnr.remark') || hasFormRemarks(dataJson)
  WRITE_FAMILIES.forEach((family) => {
    if (!sourceFamilies.includes(family)) return
    const covered = family === 'pnr.remark' ? coveredForRemarks : generatedFamilies.includes(family)
    if (!covered) {
      push('COVERAGE_MISSING_FAMILY', 'error', `El fuente ejecuta la family "${family}" y el data.json no la cubre (ni onLoad, ni postCommands, ni actions, ni forms).`, family)
    }
  })

  // 3) Lecturas del fuente sin onLoad/prefill
  if (sourceFamilies.includes('pnr.read')) {
    const hasOnLoad = Array.isArray(dataJson.onLoad) && dataJson.onLoad.length > 0
    const hasPrefill = (Array.isArray(dataJson.forms) ? dataJson.forms : []).some((form) =>
      (Array.isArray(form && form.remarks) ? form.remarks : []).some((r) => r && (r.prefillFromPnr || r.source_additional)))
    if (!hasOnLoad && !hasPrefill) {
      push('COVERAGE_MISSING_READ', 'info', 'El fuente lee del PNR/host (pnr.read) pero el data.json no trae onLoad[] ni prefill: verificá si la lectura era necesaria.')
    }
  }

  // 4) Families inventadas por el generador
  generatedFamilies.forEach((family) => {
    if (IGNORE_INVENTED.includes(family)) return
    if (!sourceFamilies.includes(family)) {
      push('COVERAGE_INVENTED_FAMILY', 'error', `El data.json emite comandos de la family "${family}" que el fuente NO tiene: no inventar efectos.`, family)
    }
  })

  // 5) Commit: el fuente no graba pero el runtime va a agregar ER (WARN_NO_COMMIT)
  const hasWrites = generatedFamilies.some((f) => WRITE_FAMILIES.includes(f)) || hasFormRemarks(dataJson)
  if (!sourceFamilies.includes('pnr.commit') && hasWrites) {
    push('COVERAGE_AUTO_COMMIT', 'warning', 'El fuente NO cierra con ER pero hay escrituras: el runtime agrega el ER automáticamente. Confirmar con negocio (WARN_NO_COMMIT).')
  }

  // 6) Alertas detectadas en el fuente que no llegaron al data.json
  const sourceAlerts = Array.isArray(triage.alertas) ? triage.alertas.length : 0
  const generatedAlerts = Array.isArray(dataJson.alerts) ? dataJson.alerts.length : 0
  if (sourceAlerts > 0 && generatedAlerts === 0) {
    push('COVERAGE_MISSING_ALERTS', 'error', `El fuente muestra ${sourceAlerts} aviso(s)/confirmación(es) al usuario y el data.json no trae alerts[]: se perderían.`)
  }

  return diagnostics
}
